import { ReadOnlyNonEmptyArray } from "./../utils/Types";
import {
  ExpressStoreKey,
  ExpressSubmissionData,
  getSectionData,
} from "./../pages/ExpressForm/express-data";
import {
  ExpressFormMenuKey,
  ExpressFormStorage,
  ExpressSectionStorage,
  getExpressData,
  getExpressSectionsData,
  getExpressSectionTargetDataByKey,
} from "@/pages/ExpressForm/express-data";
import {
  AccountNextStep,
  DocumentsDetails,
  ExpressFormMenuEntry,
  ExpressUiInfo,
  ServiceProvider,
  ServiceProviderType,
} from "@/types";
import { ExpressSubmitPageUiData } from "@/pages/ExpressForm/Submit/Submit";
import { expressDocumentRules } from "./documents-helpers";
import { getPCADataSharingConsent } from "@/components/organisms/PCADataSharingConsent";

export const expressFormMenuEntries = (
  serviceProviderType: ServiceProviderType,
  collectNokInfo: boolean,
  collectExecutorDetails: boolean,
  isCollectDocuments: boolean,
  isKyc: boolean
): ReadOnlyNonEmptyArray<ExpressFormMenuEntry> => {
  return [
    serviceProviderType === ServiceProviderType.Insurance
      ? { key: ExpressFormMenuKey.Account, label: "Policy details" }
      : { key: ExpressFormMenuKey.Account, label: "Account details" },

    { key: ExpressFormMenuKey.Notifier, label: "Your details" },
    ...(collectNokInfo ? [{ key: ExpressFormMenuKey.Nok, label: "Next of kin Details" }] : []),
    ...(collectExecutorDetails
      ? [{ key: ExpressFormMenuKey.Executor, label: "Executor Details" }]
      : []),
    ...(isCollectDocuments ? [{ key: ExpressFormMenuKey.Documents, label: "Documents" }] : []),
    ...(isKyc ? [{ key: ExpressFormMenuKey.Kyc, label: "Id verification" }] : []),
    { key: ExpressFormMenuKey.Submit, label: "Submit" },
  ];
};

// ### Truth Table

// | collectWill | sp.willNeeded | Result (res) |
// |-------------|----------------|--------------|
// | true        | true           | true         |
// | true        | false          | true         |
// | false       | true           | false        |
// | false       | false          | false        |
export function collectDocuments(
  sp: ServiceProvider,
  { willExists: collectWill, quilter }: ExpressUiInfo
) {
  const doWeNeedWill = (collectWill && sp.willNeeded) || (collectWill && !sp.willNeeded);

  const docReqByQuilter: boolean =
    quilter.isQuilter &&
    (quilter.quilterThresholdReached ||
      quilter[AccountNextStep.WithdrawalOfFundsToExecutorAccount]);

  return (
    sp.deathCertificateNeeded ||
    sp.proofOfAddressNeeded ||
    sp.grantOfRepresentationNeeded ||
    doWeNeedWill ||
    docReqByQuilter
  );
}
export function isExpressKycReq(serviceProvider: ServiceProvider) {
  return (
    !!serviceProvider.idDocumentNeeded &&
    !!getExpressSectionTargetDataByKey(ExpressFormMenuKey.Notifier)
  );
}
export function calcFormMenus(serviceProvider: ServiceProvider, uiInfo: ExpressUiInfo) {
  const { collectNokDetails, collectExecutorDetails } = uiInfo;
  const isKyc = isExpressKycReq(serviceProvider);
  const isCollectDocuments: boolean = collectDocuments(serviceProvider, uiInfo);

  const sectionData: ExpressSectionStorage = getExpressSectionsData();
  const entries: ReadOnlyNonEmptyArray<ExpressFormMenuEntry> = expressFormMenuEntries(
    serviceProvider.serviceProviderType,
    collectNokDetails,
    collectExecutorDetails,
    isCollectDocuments,
    isKyc
  );

  if (!sectionData) return entries;

  const entry = entries.map(({ key, label }) => {
    if (serviceProvider.expressMultiAccount && key === ExpressFormMenuKey.Account) {
      return formMenuByMultiAccount(sectionData, { key, label });
    } else if (!serviceProvider.expressMultiAccount && key === ExpressFormMenuKey.Account) {
      return formMenuBySingleAccount(sectionData, { key, label });
    } else if (key === ExpressFormMenuKey.Documents) {
      if (!sectionData[key]) return { key, label };

      let done = isExpressDocumentsComplete(
        uiInfo,
        serviceProvider,
        singleAccountDataForSubmission(uiInfo)
      );
      let error = sectionData[key]?.source && !done;
      return { key, label, done, error };
    } else {
      if (!sectionData[key]) return { key, label };

      let done = !!sectionData[key].target;
      let error = sectionData[key].source && !done;
      return { key, label, done, error };
    }
  });
  return entry;
}
function formMenuByMultiAccount(
  sectionData: ExpressSectionStorage,
  fme: ExpressFormMenuEntry
): ExpressFormMenuEntry {
  const deceasedSde: boolean = sourceDataExists(sectionData, ExpressStoreKey.Deceased);
  const deceasedTde: boolean = targetDataExists(sectionData, ExpressStoreKey.Deceased);
  const deceasedDone: boolean = deceasedTde;
  const deceasedError: boolean = deceasedSde && !deceasedDone;

  const multiAccountSde: boolean = sourceDataExists(sectionData, ExpressStoreKey.MultiAccount);
  const multiAccountTde: boolean = targetDataExists(sectionData, ExpressStoreKey.MultiAccount);
  const multiAccountDone: boolean =
    multiAccountTde && sectionData?.[ExpressStoreKey.MultiAccount]?.target?.length !== 0;
  // its error if sd and td does not exists
  const multiAccountError: boolean = multiAccountSde && !multiAccountDone;

  return {
    ...fme,
    done: deceasedDone && multiAccountDone,
    error: deceasedError || multiAccountError,
  };
}
function formMenuBySingleAccount(
  sectionData: ExpressSectionStorage,
  fme: ExpressFormMenuEntry
): ExpressFormMenuEntry {
  const deceasedSde: boolean = sourceDataExists(sectionData, ExpressStoreKey.Deceased);
  const deceasedTde: boolean = targetDataExists(sectionData, ExpressStoreKey.Deceased);
  const deceasedDone: boolean = deceasedTde;
  const deceasedError: boolean = deceasedSde && !deceasedDone;

  const singleAccountSde: boolean = sourceDataExists(sectionData, ExpressStoreKey.SingleAccount);
  const singleAccountTde: boolean = targetDataExists(sectionData, ExpressStoreKey.SingleAccount);
  const singleAccountDone: boolean = singleAccountTde;
  // its error if sd and td does not exists
  const singleAccountError: boolean = singleAccountSde && !singleAccountDone;

  return {
    ...fme,
    done: deceasedDone && singleAccountDone,
    error: deceasedError || singleAccountError,
  };
}
function sourceDataExists(
  sectionData: ExpressSectionStorage,
  key: ExpressFormMenuKey | ExpressStoreKey
): boolean {
  // https://www.typescriptlang.org/play/?target=9&pretty=true&noErrorTruncation=true&ts=4.7.4&importHelpers=true#code/AQSwdgLgpgTgZgQwMZWAUQB4AcZQM54BiA9jALYDKEpCA5qgN4BQwweANgK60D8AXGwgxwtANws2UJBBDEw-QcLBimAXyZMkcvBGDs+mHPiKlK1GHSgBeBh24CARAjwATB+s3bi7KADp2xLQAFADyAEYAVlIQvgAWziEA7mBB7AA0DnjRsmAOAJR5okA
  return Object.hasOwn(sectionData, key) && Object.hasOwn(sectionData[key], "source");
}

function targetDataExists(
  sectionData: ExpressSectionStorage,
  key: ExpressFormMenuKey | ExpressStoreKey
): boolean {
  // https://www.typescriptlang.org/play/?target=9&pretty=true&noErrorTruncation=true&ts=4.7.4&importHelpers=true#code/AQSwdgLgpgTgZgQwMZWAUQB4AcZQM54BiA9jALYDKEpCA5qgN4BQwweANgK60D8AXGwgxwtANws2UJBBDEw-QcLBimAXyZMkcvBGDs+mHPiKlK1GHSgBeBh24CARAjwATB+s3bi7KADp2xLQAFADyAEYAVlIQvgAWziEA7mBB7AA0DnjRsmAOAJR5okA
  return Object.hasOwn(sectionData, key) && Object.hasOwn(sectionData[key], "target");
}

// const formMenuEntries = React.useMemo(() => {
//   const data = getExpressSectionsData();
//   const entries = expressFormMenuEntries(
//     serviceProvider.serviceProviderType,
//     collectNokDetails,
//     collectExecutorDetails,
//     isCollectDocuments,
//     isKyc
//   );

//   if (!data) return entries;

//   return entries.map(({ key, label }) => {
//     if (!data[key]) {
//       return { key, label };
//     }
//     const done = !!data[key].target;
//     const error = data[key].source && !done;

//     return { key, label, done, error };
//   });
// }, [serviceProvider, collectNokDetails, collectExecutorDetails, count, isCollectDocuments, isKyc, willExists]);

export function multiAccountDataForSubmission(uiInfo: ExpressUiInfo): ExpressSubmissionData {
  const { slug, sections: sectionData }: ExpressFormStorage = getExpressData();

  const submissionData: ExpressSubmissionData = {
    slug,
    [ExpressStoreKey.MultiAccount]: getSectionData(sectionData, ExpressStoreKey.MultiAccount),
    [ExpressStoreKey.Deceased]: getSectionData(sectionData, ExpressStoreKey.Deceased),
    [ExpressStoreKey.Case]: getSectionData(sectionData, ExpressStoreKey.Case),
    [ExpressFormMenuKey.Notifier]: getSectionData(sectionData, ExpressFormMenuKey.Notifier),
    [ExpressFormMenuKey.Kyc]: getSectionData(sectionData, ExpressFormMenuKey.Kyc),
    [ExpressFormMenuKey.Documents]: getSectionData(sectionData, ExpressFormMenuKey.Documents),
  };

  if (uiInfo.collectExecutorDetails) {
    submissionData[ExpressFormMenuKey.Executor] = getSectionData(
      sectionData,
      ExpressFormMenuKey.Executor
    );
  }
  if (uiInfo.collectNokDetails) {
    submissionData[ExpressFormMenuKey.Nok] = getSectionData(sectionData, ExpressFormMenuKey.Nok);
  }

  return submissionData;
}

export function isExpressMultiAccountFormComplete(
  uiInfo: ExpressUiInfo,
  sp: ServiceProvider,
  data: ExpressSubmissionData
): boolean {
  return !(
    !data.slug ||
    !data[ExpressStoreKey.MultiAccount] ||
    data[ExpressStoreKey.MultiAccount].length === 0 ||
    !data[ExpressStoreKey.Deceased] ||
    !data[ExpressFormMenuKey.Notifier] ||
    !isExpressDocumentsComplete(uiInfo, sp, data) ||
    (sp.idDocumentNeeded && !data[ExpressFormMenuKey.Kyc])
  );
}

export function isExpressDocumentsComplete(
  uiInfo: ExpressUiInfo,
  sp: ServiceProvider,
  data: ExpressSubmissionData
): boolean {
  const documentRules = expressDocumentRules(sp, uiInfo.quilter, uiInfo.willExists, uiInfo.notifierRole);

  const isPOD = documentRules.proofOfDeath.required
    ? isDocumentIdsExist(data, "deathCertificateIds")
    : true;
  const isGOR = documentRules.grantOfRepresentation.required
    ? isDocumentIdsExist(data, "grantOfRepresentationIds")
    : true;
  const isPOA = documentRules.proofOfAddress.required
    ? isDocumentIdsExist(data, "proofOfAddressIds")
    : true;
  const isPOBA = documentRules.proofOfBankAccount.required
    ? isDocumentIdsExist(data, "proofOfBankAccountIds")
    : true;
  const isWR = documentRules.willAndProbate.required ? isDocumentIdsExist(data, "willAndProbateIds") : true;
  return isPOD && isGOR && isPOA && isPOBA && isWR;
}

function isDocumentIdsExist(data: ExpressSubmissionData, key: keyof DocumentsDetails): boolean {
  const doc_id_list = data?.[ExpressFormMenuKey.Documents]?.[key];
  if (!doc_id_list) return false;
  return doc_id_list.length !== 0;
}

export function isExpressIntestacyFormComplete(
  sp: ServiceProvider,
  data: ExpressSubmissionData,
  uiInfo: ExpressUiInfo
): [boolean, string | null] {
  // intestacy flow not impl
  if (!sp.intestacyFlow) return [true, null];
  if (uiInfo.collectExecutorDetails && !data[ExpressFormMenuKey.Executor]) {
    return [
      false,
      "Please verify that all steps(Executor) are valid (have a check on the menu) before submitting.",
    ];
  }

  if (uiInfo.collectNokDetails && !data[ExpressFormMenuKey.Nok]) {
    return [
      false,
      "Please verify that all steps(Nok information) are valid (have a check on the menu) before submitting.",
    ];
  }
  return [true, null];
}

export function singleAccountDataForSubmission(uiInfo: ExpressUiInfo): ExpressSubmissionData {
  const { slug, sections: sectionData }: ExpressFormStorage = getExpressData();
  const submissionData: ExpressSubmissionData = {
    slug,
    [ExpressStoreKey.SingleAccount]: getSectionData(sectionData, ExpressStoreKey.SingleAccount),
    [ExpressStoreKey.Deceased]: getSectionData(sectionData, ExpressStoreKey.Deceased),
    [ExpressStoreKey.Case]: {
      ...getSectionData(sectionData, ExpressStoreKey.Case),
      pcaDataSharingConsent: getPCADataSharingConsent(),
    },
    [ExpressFormMenuKey.Notifier]: getSectionData(sectionData, ExpressFormMenuKey.Notifier),
    [ExpressFormMenuKey.Kyc]: getSectionData(sectionData, ExpressFormMenuKey.Kyc),
    [ExpressFormMenuKey.Documents]: getSectionData(sectionData, ExpressFormMenuKey.Documents),
  };
  if (uiInfo.collectExecutorDetails) {
    submissionData[ExpressFormMenuKey.Executor] = getSectionData(
      sectionData,
      ExpressFormMenuKey.Executor
    );
  }
  if (uiInfo.collectNokDetails) {
    submissionData[ExpressFormMenuKey.Nok] = getSectionData(sectionData, ExpressFormMenuKey.Nok);
  }
  return submissionData;
}

export function isExpressSingleAccountFormComplete(
  uiInfo: ExpressUiInfo,
  sp: ServiceProvider,
  data: ExpressSubmissionData
): boolean {
  return !(
    !data.slug ||
    !data[ExpressStoreKey.SingleAccount] ||
    !data[ExpressStoreKey.Deceased] ||
    !data[ExpressFormMenuKey.Notifier] ||
    !isExpressDocumentsComplete(uiInfo, sp, data) ||
    (sp.idDocumentNeeded && !data[ExpressFormMenuKey.Kyc])
  );
}

export function expressDisplayData(sp: ServiceProvider): ExpressSubmitPageUiData {
  const { sections: sectionData }: ExpressFormStorage = getExpressData();
  let nok = undefined;
  let executor = undefined;
  if (sp.intestacyFlow) {
    nok = getSectionData(sectionData, ExpressFormMenuKey.Nok);
    executor = getSectionData(sectionData, ExpressFormMenuKey.Executor);
  }

  if (sp.expressMultiAccount) {
    let accounts = getSectionData(sectionData, ExpressStoreKey.MultiAccount) || [];

    return {
      notifier: getSectionData(sectionData, ExpressFormMenuKey.Notifier),
      accounts: accounts,
      nok: nok,
      executor: executor,
      deceased: getSectionData(sectionData, ExpressStoreKey.Deceased),
    };
  }
  let account = getSectionData(sectionData, ExpressStoreKey.SingleAccount);
  if (!account) account = [];
  if (account) account = [account];
  return {
    notifier: getSectionData(sectionData, ExpressFormMenuKey.Notifier),
    accounts: account,
    nok: nok,
    executor: executor,
    deceased: getSectionData(sectionData, ExpressStoreKey.Deceased),
  };
}
export const isExpressConvertedToNEForm = (caseRecord: any) => {
  return caseRecord.source === "express_form" && !caseRecord.formData.submit;
};
